import React from 'react';
import { FileSignature, PhoneCall, Building2 } from 'lucide-react';
import { SectionHeader } from '../../primitives/SectionHeader';
import { StepCard } from '../../cards/StepCard';
import { colors, space } from '../../tokens';
import { DraggableElement } from '../../../DraggableElement';
import { InlineEditableText } from '../../../InlineEditableText';

const STEPS = [
  {
    num: '01',
    icon: FileSignature,
    active: true,
    title: 'Nutzungsvertrag',
    desc: 'Im ersten Schritt schließen wir den Dachnutzungsvertrag ab. Dieser regelt die Nutzung Ihrer Dachfläche sowie die Höhe und Laufzeit Ihrer Pachteinnahmen.',
    action: null,
  },
  {
    num: '02',
    icon: PhoneCall,
    active: false,
    title: 'Anfrage beim Netzbetreiber',
    desc: 'Nach Abschluss des Vertrages prüfen wir die Netzeinspeisung und kommunizieren direkt mit Ihrem Netzbetreiber. Wir benötigen nur Ihre Vollmacht – Dauer: 6–8 Wochen.',
    action: 'Vollmacht an: info@enpower.de',
  },
];

const STEP_ICONS = {
  fileSignature: FileSignature,
  phoneCall: PhoneCall,
  building: Building2,
};

const resolveSteps = (steps) => {
  if (!Array.isArray(steps) || steps.length === 0) {
    return STEPS;
  }

  return steps.map((step, index) => ({
    ...step,
    icon: STEP_ICONS[step.icon] || STEPS[index]?.icon || PhoneCall,
    num: step.num || STEPS[index]?.num || String(index + 1).padStart(2, '0'),
    active: step.active === true,
    title: step.title || STEPS[index]?.title || '',
    desc: step.desc || STEPS[index]?.desc || '',
    action: step.action || null,
  }));
};

/**
 * NextStepsSection – "So geht's weiter" auf Page 4.
 * Enthält 2 StepCards aus derselben Komponente.
 */
export const NextStepsSection = ({ content = {}, onTextChange, editMode = false, layoutEditLevel = 'section', dragProps = {} }) => {
  const nextStepsText = {
    header: {
      eyebrow: content?.header?.eyebrow || 'Ihr Weg zu sicheren Pachteinnahmen',
      headline: content?.header?.headline || "SO GEHT'S WEITER",
    },
  };
  const steps = resolveSteps(content?.steps);

  return (
  <>
    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S4-Naechste-Schritte-Band" {...dragProps} dragAxis="y">
        <DraggableElement id="S4-Naechste-Schritte-Header" {...dragProps} dragAxis="y">
          <div style={{ padding: `${space.md + space.xs}px ${space.xxl}px 0`, background: colors.bgSoft, flexShrink: 0 }}>
            <SectionHeader
              eyebrow={<InlineEditableText as="span" value={nextStepsText.header.eyebrow} editable={editMode} onChange={(value) => onTextChange?.('page4.nextSteps.header.eyebrow', value)} />}
              headline={<InlineEditableText as="span" value={nextStepsText.header.headline} editable={editMode} onChange={(value) => onTextChange?.('page4.nextSteps.header.headline', value)} />}
            />
          </div>
        </DraggableElement>

        <div style={{ padding: `${space.sm + space.xs}px ${space.xxl}px ${space.md}px`, background: colors.bgSoft, flexShrink: 0 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: `${space.md}px`,
              height: '108px',
              boxSizing: 'border-box',
            }}
          >
            {steps.map((s, i) => (
              <DraggableElement key={i} id={`S4-Naechster-Schritt-${i + 1}`} {...dragProps} dragAxis="y">
                <StepCard num={s.num} icon={s.icon} active={s.active} title={s.title} desc={s.desc} action={s.action} />
              </DraggableElement>
            ))}
          </div>
        </div>
      </DraggableElement>
    ) : (
      <>
        <div style={{ padding: `${space.md + space.xs}px ${space.xxl}px 0`, background: colors.bgSoft, flexShrink: 0 }}>
          <SectionHeader
            eyebrow={<InlineEditableText as="span" value={nextStepsText.header.eyebrow} editable={editMode} onChange={(value) => onTextChange?.('page4.nextSteps.header.eyebrow', value)} />}
            headline={<InlineEditableText as="span" value={nextStepsText.header.headline} editable={editMode} onChange={(value) => onTextChange?.('page4.nextSteps.header.headline', value)} />}
          />
        </div>

        <div style={{ padding: `${space.sm + space.xs}px ${space.xxl}px ${space.md}px`, background: colors.bgSoft, flexShrink: 0 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: `${space.md}px`,
              height: '108px',
              boxSizing: 'border-box',
            }}
          >
            {steps.map((s, i) => (
              <StepCard key={i} num={s.num} icon={s.icon} active={s.active} title={s.title} desc={s.desc} action={s.action} />
            ))}
          </div>
        </div>
      </>
    )}

    <div style={{ height: '1px', background: colors.border, flexShrink: 0, margin: `0 ${space.xxl}px` }} />
  </>
  );
};
